import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Layout from "../components/Layout";
import { Users, Clock, Calendar, CalendarOff, Plus, ArrowRight, CreditCard } from "lucide-react";
import { fmtRange } from "../lib/dates";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    employee_count: 0,
    shift_count: 0,
    roster_count: 0,
    pending_time_off: 0
  });
  const [recentRosters, setRecentRosters] = useState([]);
  const [subData, setSubData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, rostersRes, subRes, pricingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/rosters`, { withCredentials: true }),
        axios.get(`${API}/subscription/status`, { withCredentials: true }),
        axios.get(`${API}/subscription/config`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentRosters(rostersRes.data.slice(0, 5));
      setSubData(subRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const symbol = (c) => ({ usd: "$", aud: "A$", eur: "€", gbp: "£", cad: "C$", nzd: "NZ$" }[c] || "$");
  const priceLabel = () => {
    if (!pricing) return "";
    return `${symbol(pricing.currency)}${Number(pricing.price).toFixed(Number.isInteger(Number(pricing.price)) ? 0 : 2)}/month`;
  };

  const getDaysRemaining = () => {
    // Prefer explicit subscription_expires; fall back to trial_start + 30 days for new email/password signups
    let target = subData?.subscription_expires;
    if (!target && (user?.trial_start || subData?.trial_start)) {
      const ts = new Date(user?.trial_start || subData?.trial_start);
      ts.setDate(ts.getDate() + 30);
      target = ts.toISOString();
    }
    if (!target) return 30;
    const expires = new Date(target);
    const now = new Date();
    return Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
  };

  const statCards = [
    { label: "Employees", value: stats.employee_count, icon: Users, path: "/employees" },
    { label: "Shift Types", value: stats.shift_count, icon: Clock, path: "/shifts" },
    { label: "Rosters", value: stats.roster_count, icon: Calendar, path: "/rosters" },
    { label: "Pending Time Off", value: stats.pending_time_off, icon: CalendarOff, path: "/time-off" }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Here's an overview of your roster management
            </p>
          </div>
          <Button
            onClick={() => navigate("/rosters")}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="create-roster-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Roster
          </Button>
        </div>

        {/* Subscription Banner */}
        {subData && (subData.subscription_status === "trial" || subData.subscription_status === "expired") && (
          <div className={`
            flex items-center justify-between p-4 border
            ${subData.subscription_status === "expired" 
              ? 'bg-red-50 border-red-200' 
              : 'bg-blue-50 border-blue-200'}
          `}>
            <div className="flex items-center gap-3">
              <CreditCard className={`w-5 h-5 ${subData.subscription_status === "expired" ? 'text-red-600' : 'text-blue-600'}`} />
              <div>
                <p className={`text-sm font-medium ${subData.subscription_status === "expired" ? 'text-red-800' : 'text-blue-800'}`}>
                  {subData.subscription_status === "expired"
                    ? "Your subscription has expired"
                    : `Free trial — ${getDaysRemaining()} days remaining`
                  }
                </p>
                <p className={`text-xs ${subData.subscription_status === "expired" ? 'text-red-600' : 'text-blue-600'}`}>
                  {subData.subscription_status === "expired"
                    ? "Subscribe now to regain access to all features"
                    : pricing ? `Subscribe at ${priceLabel()} to continue after your trial` : "Subscribe to continue after your trial"
                  }
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/subscription")}
              className={
                subData.subscription_status === "expired"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-[#002FA7] hover:bg-[#00227A] text-white"
              }
              data-testid="dashboard-subscribe-btn"
            >
              {subData.subscription_status === "expired" ? "Subscribe Now" : "View Plans"}
            </Button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(stat.path)}
                className="bg-white dark:bg-zinc-900 p-6 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                  <ArrowRight className="w-4 h-4 text-zinc-300" />
                </div>
                <p className="text-3xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{stat.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mt-1">
                  {stat.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate("/employees")}
            className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 text-left hover:border-zinc-300 transition-colors"
            data-testid="quick-add-employee"
          >
            <Users className="w-5 h-5 text-[#002FA7] mb-3" />
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Add Employee</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Add team members to your roster</p>
          </button>
          
          <button
            onClick={() => navigate("/shifts")}
            className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 text-left hover:border-zinc-300 transition-colors"
            data-testid="quick-add-shift"
          >
            <Clock className="w-5 h-5 text-[#002FA7] mb-3" />
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Configure Shifts</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Define shift types and hours</p>
          </button>
          
          <button
            onClick={() => navigate("/rules")}
            className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 text-left hover:border-zinc-300 transition-colors"
            data-testid="quick-add-rule"
          >
            <Calendar className="w-5 h-5 text-[#002FA7] mb-3" />
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Set Rules</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Configure scheduling rules</p>
          </button>
        </div>

        {/* Recent Rosters */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
          <div className="p-6 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
            <div>
              <h2 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">Recent Rosters</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Your latest roster schedules</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/rosters")}
              className="text-sm text-[#002FA7] hover:text-[#00227A]"
              data-testid="view-all-rosters"
            >
              View All
            </Button>
          </div>
          
          {recentRosters.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No rosters yet</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Create your first roster to get started</p>
              <Button
                onClick={() => navigate("/rosters")}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
                data-testid="empty-create-roster"
              >
                Create Roster
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {recentRosters.map((roster) => (
                <button
                  key={roster.roster_id}
                  onClick={() => navigate(`/rosters/${roster.roster_id}`)}
                  className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors text-left"
                  data-testid={`roster-${roster.roster_id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{roster.name}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                      {fmtRange(roster.start_date, roster.end_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`
                      text-xs font-medium px-2 py-1 rounded
                      ${roster.status === 'published' 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                      }
                    `}>
                      {roster.status}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
